import React, { useState } from 'react';
import { connectionInvitationsApi } from '../utils/api';

// Minimal invite-counterparty form. Always uses the email-token invitation
// flow — we don't bother looking up whether the email maps to an existing
// workspace. If it does, the recipient signs in and accepts; if not, the
// landing page invites them to sign up inline. One flow for both cases
// keeps the UX predictable.
export default function InviteCounterpartyModal({ onClose, onInvited }) {
  const [form, setForm] = useState({
    myRole: 'seller',
    invitedEmail: '',
    invitedWorkspaceName: '',
    contextNote: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const res = await connectionInvitationsApi.create(form);
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send invitation');
    } finally {
      setSaving(false);
    }
  };

  if (result) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
        <div className="bg-white rounded-xl p-6 max-w-lg w-full mx-4" onClick={(e) => e.stopPropagation()}>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Invitation sent</h3>
          <p className="text-sm text-gray-600 mb-4">
            {form.invitedEmail} will receive an email with a link to accept.
          </p>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
            <p className="text-xs text-gray-600 mb-1">Direct link (dev)</p>
            <p className="text-xs text-gray-700 font-mono break-all">{result.inviteUrl}</p>
          </div>
          <p className="text-xs text-gray-600 mb-4">
            Expires {new Date(result.invitation.expires_at).toLocaleString()}.
          </p>
          <div className="flex justify-end gap-3">
            <button onClick={onInvited} className="btn-primary">Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-xl p-6 max-w-lg w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Invite a counterparty</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">{error}</div>}

          <div>
            <label className="label">Your role in this relationship</label>
            <div className="flex gap-3">
              {['seller', 'buyer'].map((r) => (
                <label key={r} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="myRole"
                    value={r}
                    checked={form.myRole === r}
                    onChange={() => setForm({ ...form, myRole: r })}
                  />
                  <span className="text-sm">{r === 'seller' ? 'Agency (we deliver outcomes)' : 'Client (we buy outcomes)'}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label htmlFor="invitedEmail" className="label">Counterparty email *</label>
            <input
              id="invitedEmail"
              type="email"
              required
              className="input"
              value={form.invitedEmail}
              onChange={(e) => setForm({ ...form, invitedEmail: e.target.value })}
              placeholder="client@acmebrands.com"
            />
          </div>

          <div>
            <label htmlFor="invitedWorkspaceName" className="label">Their workspace name (optional)</label>
            <input
              id="invitedWorkspaceName"
              type="text"
              className="input"
              value={form.invitedWorkspaceName}
              onChange={(e) => setForm({ ...form, invitedWorkspaceName: e.target.value })}
              placeholder="Acme Law"
            />
            <p className="mt-1 text-xs text-gray-600">Shown on the invite landing page; invitee can change it when accepting.</p>
          </div>

          <div>
            <label htmlFor="contextNote" className="label">Note (optional)</label>
            <textarea
              id="contextNote"
              rows={3}
              className="input"
              value={form.contextNote}
              onChange={(e) => setForm({ ...form, contextNote: e.target.value })}
              placeholder="A short message about what this relationship is for."
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary">
              {saving ? 'Sending...' : 'Send invitation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
